import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Download } from 'lucide-react';
import { reasonText } from '../pages/collectionMonitor';

// [[Characteristic Collection]] #obsidian-marker
export default function DocumentCharacteristicsButton({ kind, documentId, disabled = false }) {
    const [supplier, setSupplier] = useState('digikey');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [progress, setProgress] = useState(null);
    const [progressError, setProgressError] = useState('');
    const [revision, setRevision] = useState(0);
    const generation = useRef(0);
    useEffect(() => {
        setMessage(''); setError(''); setBusy(false); setProgress(null);
    }, [kind, documentId, supplier]);
    useEffect(() => {
        const current = ++generation.current;
        let timer;
        const controller = new AbortController();
        const refresh = async () => {
            try {
                const { data } = await axios.get(`/api/catalog-characteristics/${kind}/${documentId}/progress`, {
                    params: { supplier }, signal: controller.signal,
                });
                if (current !== generation.current) return;
                setProgress(data); setProgressError('');
                if (Number(data.pending) + Number(data.running) + Number(data.waiting) > 0) timer = setTimeout(refresh, 5000);
            } catch (e) {
                if (current !== generation.current) return;
                setProgressError('Не удалось обновить прогресс. Повторная проверка через 10 секунд; задания продолжают выполняться.');
                timer = setTimeout(refresh, 10000);
            }
        };
        setProgressError('');
        if (documentId) refresh();
        return () => { generation.current++; clearTimeout(timer); controller.abort(); };
    }, [kind, documentId, supplier, revision]);
    async function start() {
        if (busy || !documentId) return;
        const current = generation.current;
        setBusy(true); setMessage(''); setError('');
        try {
            const { data } = await axios.post(`/api/catalog-characteristics/${kind}/${documentId}`, { supplier });
            if (current !== generation.current) return;
            setMessage(`Поставлено в очередь: ${data.queued}. Уже заполнены: ${data.complete}. Ранее запрашивались: ${data.previous_attempt}.`);
            setRevision(value => value + 1);
        } catch (e) {
            if (current === generation.current) setError(e.response?.data?.message || 'Не удалось запустить загрузку характеристик.');
        } finally { if (current === generation.current) setBusy(false); }
    }
    const active = progress && Number(progress.pending) + Number(progress.running) + Number(progress.waiting) > 0;
    const percent = progress?.total ? Math.round(100 * progress.processed / progress.total) : 0;
    return <div className="documentCharacteristics">
        <div className="documentCharacteristicsControls">
            <select aria-label="Поставщик характеристик" value={supplier} disabled={busy} onChange={e => setSupplier(e.target.value)}>
                <option value="digikey">DigiKey</option><option value="mouser">Mouser</option><option value="chipdip">Чип и Дип</option>
            </select>
            <button type="button" className="sourceBtn" disabled={disabled || busy || active || !documentId} onClick={start}
                title="Для всего документа. Заполненные характеристики и прежние попытки пропускаются; картинки и PDF не изменяются.">
                <Download size={16} />{busy ? 'Постановка в очередь…' : active ? 'Характеристики загружаются…' : 'Загрузить хар-ки'}
            </button>
        </div>
        <div role="status" aria-live="polite">
            {message && <small>{message}</small>}
            {progress?.total > 0 && <div>
                <small>Задания по документу: обработано {progress.processed} из {progress.total} ({percent}%). {active ? 'В работе.' : 'Обработка завершена.'}</small>
                <progress aria-label="Обработано характеристик" max={progress.total} value={progress.processed} style={{display:'block',width:'100%'}} />
                <small>Загружено: {progress.done}. Не найдено: {progress.not_found}. Ошибок: {progress.failed}. В очереди: {progress.pending}. Выполняется: {progress.running}. Ожидают: {progress.waiting}.</small>
                {(progress.waits || []).map(item => <div key={item.reason}><small>{item.reason === 'IMPORT_BUSY' ? 'Ожидание: общий импорт занят, обработка продолжится автоматически' : reasonText(item.reason)} — {item.count}.</small></div>)}
            </div>}
        </div>
        {progressError && <small role="alert">{progressError}</small>}
        {error && <small role="alert">{error}</small>}
    </div>;
}
